import { z } from "zod";

const fields = { name: z.string().trim().min(1).max(100) };
export const IdentityConnectionInput = z
  .object({
    ...fields,
    protocol: z.literal("saml"),
    metadataSourceUrl: z.string().url().max(2048).optional(),
    metadataXml: z.string().min(1).max(100_000),
    // An absent choice preserves role-based admission for existing saved revisions.
    requesterAccess: z.enum(["role", "company"]).optional(),
    roleAttribute: z.string().trim().max(300).optional(),
    roleValue: z.string().trim().max(300).optional(),
    emailAttribute: z.string().trim().min(1).max(300),
    nameAttribute: z.string().trim().min(1).max(300),
  })
  .strict()
  .superRefine((input, context) => {
    if (input.requesterAccess === "company") return;
    for (const field of ["roleAttribute", "roleValue"] as const) {
      if (!input[field]) context.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: "Required when requester access uses a company role" });
    }
  });
export type IdentityConnectionInput = z.infer<typeof IdentityConnectionInput>;
export const ConnectionTestInvitationToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const ConnectionTestCode = z.enum([
  "waiting",
  "expired",
  "verified",
  "response_invalid",
  "identity_invalid",
  "requester_missing",
  "email_missing",
  "configuration_changed",
  "start_failed",
  "verification_failed",
  "unknown_failure",
]);
export type ConnectionTestCode = z.infer<typeof ConnectionTestCode>;
export const ConnectionTestDiagnostic = z.enum([
  "signing_certificate_mismatch",
  "signature_invalid",
  "assertion_signature_required",
  "assertion_time_invalid",
  "issuer_mismatch",
  "audience_mismatch",
  "recipient_mismatch",
  "request_mismatch",
  "state_invalid",
  "idp_name_id_policy",
  "idp_rejected",
  "response_format_invalid",
  "response_encrypted",
  "algorithm_rejected",
  "unclassified",
]);
export type ConnectionTestDiagnostic = z.infer<typeof ConnectionTestDiagnostic>;
export const ConnectionTestResult = z.object({
  id: z.string().uuid(),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  code: ConnectionTestCode,
  diagnostic: ConnectionTestDiagnostic.optional(),
});
export type ConnectionTestResult = z.infer<typeof ConnectionTestResult>;
export type IdentityConnectionView = {
  id: string;
  tenantId: string;
  name: string;
  protocol: "saml";
  enabled: boolean;
  revision: number;
  activeRevision: number;
  tested: boolean;
  lastTest?: ConnectionTestResult | null;
  issuer: string;
  configuration: IdentityConnectionInput;
  entityId: string;
  acsUrl: string;
  metadataUrl: string;
  loginUrl: string;
  certificate: string | null;
  readOnly: boolean;
  activeMetadataUrl?: string;
  trustUsable?: boolean;
  metadataMaintenance?: {
    sourceUrl: string;
    lastAttemptAt: string | null;
    lastSuccessAt: string | null;
    nextRefreshAt: string | null;
    failure: string | null;
    pendingChanges: boolean;
  };
  idpCertificateExpiries?: string[];
  spCertificateExpiry?: string | null;
};
// Portal-side SAML values the company needs before a connection draft exists.
export type IdentityServiceProvider = { entityId: string; acsUrl: string };
export type IdentityConnectionList = { connections: IdentityConnectionView[]; serviceProvider: IdentityServiceProvider };
export type IdentityConnectionResult = { connection: IdentityConnectionView };
